/**
 * AdminNotificationsContext — provider globale che apre UNA sola connessione
 * SSE verso ``/admin/notifications/stream`` e la distribuisce a tutti i
 * subscriber (bell della navbar, pannello Compiti Assegnati, ecc.).
 *
 * API:
 *  - useAdminNotificationsCtx() -> { subscribe(cb) -> unsubscribe, sseOpen }
 *
 * Il provider stesso è no-op per utenti non-admin (non apre nulla).
 */
import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useAdminNotifications } from "../hooks/useAdminNotifications";

const Ctx = createContext(null);

export function AdminNotificationsProvider({ user, children }) {
  const listenersRef = useRef(new Set());
  const [sseOpen, setSseOpen] = useState(false);
  const isAdmin = !!user && user.role === "admin";

  const dispatch = useCallback((event) => {
    for (const cb of listenersRef.current) {
      try { cb(event); } catch { /* ignore listener error */ }
    }
  }, []);

  useAdminNotifications({
    enabled: isAdmin,
    onStatus: (s) => setSseOpen(s === "open"),
    onEvent: dispatch,
  });

  const subscribe = useCallback((cb) => {
    listenersRef.current.add(cb);
    return () => listenersRef.current.delete(cb);
  }, []);

  const value = { subscribe, sseOpen };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAdminNotificationsCtx() {
  const v = useContext(Ctx);
  // Se non c'è provider (utente non-admin), ritorniamo un no-op consumer
  if (!v) return { subscribe: () => () => {}, sseOpen: false };
  return v;
}

/**
 * Helper: sottoscrivi ai soli eventi che matchano ``typeFilter`` (stringa
 * esatta o array di prefissi tipo ``["assigned_task."]``).
 */
export function useAdminNotificationEvents(typeFilter, callback) {
  const { subscribe } = useAdminNotificationsCtx();
  const cbRef = useRef(callback);
  useEffect(() => { cbRef.current = callback; }, [callback]);

  useEffect(() => {
    return subscribe((ev) => {
      const t = ev?.type || "";
      if (typeFilter) {
        const filters = Array.isArray(typeFilter) ? typeFilter : [typeFilter];
        const match = filters.some((f) => (f.endsWith(".") ? t.startsWith(f) : t === f));
        if (!match) return;
      }
      cbRef.current?.(ev);
    });
  }, [subscribe, typeFilter]);
}
